var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { GatedFeatName } from "gather-client-common/dist/src/public/gatekeeping/FEATURES";
import isEqual from "lodash/isEqual";
import { getActiveVideoElements, isAdaptiveStreamSettings, RemoteBandwidthManager, } from "./RemoteBandwidthManager";
import { BasePeerManager } from "../../BasePeerManager";
import { config } from "../../config";
import { debugLogMajorEvent, debugMajorEvents } from "../../DebugLogger";
import { metricsManager } from "../../metrics/MetricsManager";
import { TransportProduceError } from "./SignaledTransport";
import { prefixKeys, ProduceState } from "../../utils";
import { gatherVideoClientDataSource, metricsManager as globalsMetricsManager, } from "../../Globals";
import { Logger } from "../../Logger";
import { ConsumerInfo, ConsumerState, MigrationState } from "../../ConsumerInfo";
import { BACKUP_SFU_LIFESPAN_MS, SFUClientManager, } from "./SFUClientManager";
import { VideoPerformanceStages, videoPerformanceTracker } from "../../videoPerformance";
import { PeerManagerEvent } from "../../PeerManagerEvent";
import { timeout } from "gather-common/dist/src/public/promises";
import { LoggableMethodFactory } from "../../log.decorator";
import { debounce } from "lodash";
import RouterClient from "./RouterClient";
import { ErrorContext } from "gather-common/dist/src/public/utils";
import { BitrateManager } from "./BitrateManager";
import { DEFAULT_VIDEO_CAPTURE_HEIGHT, DEFAULT_VIDEO_CAPTURE_WIDTH, } from "gather-video-common/dist/src/public/settings";
import { VIDEO_SYSTEM_AUTOMATED_REPORT } from "gather-common/dist/src/public/av";
import { GVCEventEmitter, GVCInternalEvent } from "../../EventEmitter/GVCEventEmitter";
import { WebRTCTransportErrorCode, } from "../../EventEmitter/WebRTCTransportEvents";
import { GVCInternalMediasoupEvent, } from "../../EventEmitter/MediasoupEvents";
import { IceRestartReason } from "./IceRestartReason";
export { BACKUP_SFU_LIFESPAN_MS } from "./SFUClientManager";
const LoggableMethod = LoggableMethodFactory("GatherPeerManager");
export class GatherPeerManager extends BasePeerManager {
    id = "UNINITIALIZED";
    spaceId = "";
    _eventEmitter = new GVCEventEmitter();
    eventEmitterCleanupFns = [];
    _routerClient;
    _sfuClientManager;
    producedTracks = new Map();
    producePaused = new Map();
    muted = {};
    mutedAll = new Set();
    allowed = {};
    maxConsumerSpatialLayer = {};
    maxConsumerTemporalLayer = {};
    chooseSFUStrategy = "filtering";
    info = {};
    produceScores = {
        audio: undefined,
        video: undefined,
        screen: undefined,
        sound: undefined,
    };
    canRetryProduceStartUponEndedTrack = new Set(["audio", "video"]);
    bandwidthManager = new RemoteBandwidthManager();
    bitrateManager;
    localVideoCaptureResolution = {
        width: DEFAULT_VIDEO_CAPTURE_WIDTH,
        height: DEFAULT_VIDEO_CAPTURE_HEIGHT,
    };
    isAVInterruptible = true;
    setLocalVideoCaptureResolution = (width, height) => {
        const resolutionChanged = width !== this.localVideoCaptureResolution.width ||
            height !== this.localVideoCaptureResolution.height;
        this.localVideoCaptureResolution = { width, height };
        const track = this.producedTracks.get("video");
        const pause = this.producePaused.get("video");
        if (resolutionChanged && track && pause !== undefined) {
            this.produceStart("video", track, pause);
        }
    };
    setIsAVInterruptible(isAVInterruptible) {
        const becameInterruptible = !this.isAVInterruptible && isAVInterruptible;
        this.isAVInterruptible = isAVInterruptible;
        if (!this._routerClient)
            return;
        if (this._routerClient.getOwnSfuStatus() === "cordoned" && becameInterruptible) {
            this._routerClient.moveSFU();
        }
    }
    hasRunCloudflareTurnTest = false;
    constructor(statsManager) {
        super(statsManager);
        this.rtcStatsManager = statsManager;
        this.bitrateManager = new BitrateManager((playerId, paused) => this.setStreamStatePaused(playerId, paused));
        this.bandwidthManager.setMaxConsumerLayers = this.setMaxConsumerLayersFiltered.bind(this);
        this.bitrateManager.setMaxConsumerLayers = this.setMaxConsumerLayers.bind(this);
        this.bandwidthManager.setHighestPriorityStreams = this.setHighestPriorityStreams.bind(this);
    }
    setAdaptiveStreamSettings(_options) {
        if (isAdaptiveStreamSettings(_options)) {
            this.bandwidthManager.setAdaptiveStreamSettings(_options);
        }
    }
    setAdaptiveStream(active) {
        this.bandwidthManager.adaptiveStreams = active;
    }
    startObserve(id, elementInfo, kind) {
        if (kind !== "video" || !this.info[id])
            return;
        if (this.videoElementInfo.get(id) === elementInfo) {
            Logger.info("Applying same elementInfo more than once");
            return;
        }
        const debouncedHandleResize = debounce(() => {
            this.bandwidthManager.setAdaptiveStreamDesiredResolution(id, "video", elementInfo.width(), elementInfo.height());
        }, 100);
        elementInfo.handleResize = () => {
            debouncedHandleResize();
        };
        elementInfo.observe();
        this.videoElementInfo.set(id, elementInfo);
    }
    stopObserve(id, kind) {
        if (kind !== "video")
            return;
        const idInfo = this.info[id];
        if (idInfo?.streamStatePaused.video)
            return;
        const elementInfo = this.videoElementInfo.get(id);
        elementInfo?.stopObserving();
        this.videoElementInfo.delete(id);
    }
    getInfo(id) {
        return this.info[id];
    }
    onDisableVideo() { }
    async produceStart(kind, track, pause, sfuOverride) {
        this.producedTracks.set(kind, track);
        this.producePaused.set(kind, pause);
        if (kind === "video") {
            const trackSettings = track.getSettings();
            this.localVideoCaptureResolution = {
                width: trackSettings.width ?? DEFAULT_VIDEO_CAPTURE_WIDTH,
                height: trackSettings.height ?? DEFAULT_VIDEO_CAPTURE_HEIGHT,
            };
        }
        if (track.readyState === "ended") {
            if (this.canRetryProduceStartUponEndedTrack.has(kind)) {
                this.canRetryProduceStartUponEndedTrack.delete(kind);
                Logger.error("track ended detected, start retry flow", { kind });
                setTimeout(() => {
                    this.publishEvent(PeerManagerEvent.ProducerBadTrack, track.kind === "audio" ? "audio" : "video");
                }, 0);
            }
            else {
                this._onProduceStateChange(kind, ProduceState.Errored);
                this.canRetryProduceStartUponEndedTrack.add(kind);
            }
            return;
        }
        this.canRetryProduceStartUponEndedTrack.add(kind);
        const sfuOverrideClient = this._sfuClientManager?.getSFUByAddr(sfuOverride ?? "");
        if (sfuOverride && !sfuOverrideClient)
            return;
        const sfus = sfuOverrideClient
            ? [sfuOverrideClient]
            : this._sfuClientManager?.getSFUsByPlayerId(this.id);
        if (!sfus)
            return;
        if (sfus.length === 0) {
            this._onProduceStateChange(kind, ProduceState.Loading);
            Logger.log(`no sfu attached for producing ${kind}`);
            return;
        }
        const promises = sfus.map(async (sfu) => {
            const addr = sfu.serverURL;
            this._onProduceStateChange(kind, ProduceState.Loading, addr);
            try {
                const success = await sfu.produceStart(kind, track, pause);
                if (success) {
                    this._onProduceStateChange(kind, ProduceState.Connected, addr);
                    Logger.log(`Successfully produced ${kind} on ${addr}`);
                    if (kind === "audio") {
                        this.rtcStatsManager.enableProducedAudioLevelTracking(pause ? false : true);
                    }
                }
                else {
                    Logger.log(`Cancelled produce ${kind} on ${addr}`);
                }
            }
            catch (e) {
                if (e instanceof TransportProduceError) {
                    this._onProduceStateChange(kind, ProduceState.Errored, addr);
                }
                if (e instanceof Error) {
                    Logger.error(`produce start error for kind on addr`, new ErrorContext({
                        kind,
                        addr,
                    }, e));
                }
                else {
                    Logger.error(`produce start error for kind on addr`, new ErrorContext({ kind, addr }));
                }
            }
        });
        await Promise.all(promises);
    }
    produceEnd(kind) {
        if (kind === "audio") {
            this.rtcStatsManager.enableProducedAudioLevelTracking(false);
        }
        this.producedTracks.delete(kind);
        this.producePaused.delete(kind);
        this.updateProduceScore(kind, undefined);
        this.stopProducing(kind);
    }
    stopProducing(kind, sfuOverride) {
        const sfus = sfuOverride ? [sfuOverride] : this._sfuClientManager?.getSFUsByPlayerId(this.id);
        if (!sfus)
            return;
        if (sfus.length === 0) {
            this._onProduceStateChange(kind, ProduceState.Ended);
            return;
        }
        sfus.forEach((sfu) => {
            sfu.produceEnd(kind);
            this._onProduceStateChange(kind, ProduceState.Ended, sfu.serverURL);
        });
    }
    produceResume(kind) {
        this.producePaused.set(kind, false);
        this._sfuClientManager?.getSFUsByPlayerId(this.id)?.forEach((sfu) => sfu.produceResume(kind));
        if (kind === "audio") {
            this.rtcStatsManager.enableProducedAudioLevelTracking(true);
        }
    }
    producePause(kind) {
        if (kind === "audio") {
            this.rtcStatsManager.enableProducedAudioLevelTracking(false);
        }
        this.producePaused.set(kind, true);
        this._sfuClientManager?.getSFUsByPlayerId(this.id)?.forEach((sfu) => sfu.producePause(kind));
    }
    onRouterConnect() {
        this._sfuClientManager?.start();
    }
    onRouterDisconnect() {
        this._sfuClientManager?.stop();
    }
    onBandwidthUtilization(event) {
        const { sfuAddr, direction, available, used } = event;
        this.publishEvent(PeerManagerEvent.BandwidthUtilization, sfuAddr, direction, available, used);
    }
    onPacketsNotReceived(context) {
        const { playerId, kind } = context;
        const sfuClient = this._sfuClientManager?.getPrimarySFUByPlayerId(playerId);
        sfuClient?.debugConsumer(playerId, this.spaceId, [kind]);
    }
    onConsumerDestroyed(context) {
        const { playerId, kind } = context;
        if (kind === "video") {
            delete this.maxConsumerSpatialLayer[playerId];
            delete this.maxConsumerTemporalLayer[playerId];
        }
    }
    onConsumerEnabled(context) {
        const { playerId, kind } = context;
        if (kind === "video") {
            const idInfo = this.info[playerId];
            if (idInfo && idInfo.streamStatePaused.video === true) {
                idInfo.streamStatePaused = { screen: false, video: false };
                this._dispatchInfo();
            }
            this.bitrateManager.consumerEnabled(playerId);
        }
    }
    start(id, spaceId, eventEmitter) {
        this.id = id;
        this.spaceId = spaceId;
        this._eventEmitter = eventEmitter;
        this.setSFUChoosingStrategy();
        this.eventEmitterCleanupFns.forEach((f) => f());
        this.eventEmitterCleanupFns = [
            this._eventEmitter.addEventListener(GVCInternalEvent.WebRTCTransportConnectionStateChange, this.onWebRTCTransportConnectionStateChange),
            this._eventEmitter.addEventListener(GVCInternalEvent.RouterDisconnected, this.onRouterDisconnect.bind(this)),
            this._eventEmitter.addEventListener(GVCInternalEvent.RouterConnected, this.onRouterConnect.bind(this)),
            this._eventEmitter.addEventListener(GVCInternalEvent.RouterAddrs, this.onAddrs.bind(this)),
            this._eventEmitter.addEventListener(GVCInternalEvent.RouterCordonSFU, this.onCordonSFU.bind(this)),
            this._eventEmitter.addEventListener(GVCInternalEvent.BandwidthUtilization, this.onBandwidthUtilization.bind(this)),
            this._eventEmitter.addEventListener(GVCInternalMediasoupEvent.PacketsNotReceived, this.onPacketsNotReceived.bind(this)),
            this._eventEmitter.addEventListener(GVCInternalMediasoupEvent.ConsumerDestroyed, this.onConsumerDestroyed.bind(this)),
            this._eventEmitter.addEventListener(GVCInternalMediasoupEvent.ConsumerEnabled, this.onConsumerEnabled.bind(this)),
        ];
        this._routerClient = new RouterClient(id, spaceId, config.routerURLs, this._eventEmitter, () => this._sfuClientManager?.getPrimarySFUByPlayerId(this.id)?.isProducing() || false, this.chooseSFUStrategy === "scoring");
        this._sfuClientManager = new SFUClientManager(this.id, this.spaceId, this._eventEmitter, (userId) => {
            this._routerClient?.sendGetAddrs(userId);
        }, (userId) => {
            this._routerClient?.sendUnsubscribe(userId);
        }, this.playerConnectedSFU.bind(this), this.playerLeavingSFU.bind(this), this.rtcStatsManager);
        const getAppContext = () => ({
            consumingScreenShareCount: metricsManager.vc.mediasoup.consumerCount("screen"),
        });
        const getSfuClients = () => {
            const minimalSfuClients = {};
            const sfuClients = this._sfuClientManager?.sfuClients ?? {};
            Object.entries(sfuClients).forEach(([sfu, sfuClient]) => {
                minimalSfuClients[sfu] = {
                    serverURL: sfuClient.serverURL,
                    sendTransport: sfuClient.sendTransport?.transport,
                    recvTransport: sfuClient.recvTransport?.transport,
                };
            });
            return minimalSfuClients;
        };
        this.bitrateManager.start(this.id, this._sfuClientManager, this.rtcStatsManager, true);
        this.rtcStatsManager.enableStatsCollection(getSfuClients, getAppContext);
    }
    setSFUChoosingStrategy() {
        const isSfuScoringEnabled = gatherVideoClientDataSource.gate(GatedFeatName.ChooseSfuByScoring, this.id, this.spaceId);
        this.chooseSFUStrategy = isSfuScoringEnabled ? "scoring" : "filtering";
    }
    _DEBUG_increaseSpatialLayer() {
        const sfuClients = this._sfuClientManager?.sfuClients ?? {};
        for (const sfu of Object.values(sfuClients)) {
            sfu.producers?.get("video")?.setMaxSpatialLayer(1);
        }
    }
    _DEBUG_REPLAY_VIDEOS() {
        const videos = document.querySelectorAll("video");
        videos.forEach((video) => video.play());
    }
    restart() {
        const sfuClients = this._sfuClientManager?.sfuClients ?? {};
        for (const sfu of Object.values(sfuClients)) {
            sfu.reload();
        }
    }
    destroy() {
        debugLogMajorEvent(`PeerManager destroyed!\n${new Error().stack}`);
        this.eventEmitterCleanupFns.forEach((f) => f());
        this.eventEmitterCleanupFns = [];
        this._routerClient?.destroy();
        this._sfuClientManager?.destroy();
        this.info = {};
        this._onSFUReloadChange(false);
        this._dispatchInfo();
        this.muted = {};
        this.bitrateManager.destroy();
    }
    subscribe(id, tagSet) {
        const prevTagSet = this._sfuClientManager?.getSubscribedTagsByPlayerId(id);
        if (isEqual(prevTagSet, tagSet))
            return;
        this._sendRemoteLog(`Subscribe ${id} ${Array.from(tagSet).join(" ")}`);
        const videoTagChangedTo = tagSet.has("video") !== prevTagSet?.has("video") ? tagSet.has("video") : undefined;
        if (!this.muted[id]) {
            this.muted[id] = new Set(this.mutedAll);
        }
        const infoExists = this.info[id] ? true : false;
        const idInfo = this.info[id] ?? new ConsumerInfo(ConsumerState.ConnectingToSFU);
        if (!infoExists) {
            this.muted[id]?.forEach((kind) => {
                idInfo.muted[kind] = true;
            });
            this.info[id] = idInfo;
        }
        this._sfuClientManager?.addPlayer(id, tagSet);
        const sfus = this._sfuClientManager?.getSFUsByPlayerId(id) ?? [];
        if (sfus.length === 0) {
            videoPerformanceTracker.markStatic(VideoPerformanceStages.RouterGetAddr, id);
            idInfo.state = ConsumerState.WaitingForSFUAddress;
            this.bitrateManager.updateVideoSubscribed(undefined, tagSet.has("video"));
        }
        else {
            sfus.forEach((sfu) => {
                sfu.subscribe(id, tagSet);
                if (videoTagChangedTo !== undefined) {
                    this.bitrateManager.updateVideoSubscribed(sfu.serverURL, videoTagChangedTo);
                }
            });
        }
        this._dispatchInfo();
    }
    unsubscribe(id) {
        const sfuClient = this._sfuClientManager?.getPrimarySFUByPlayerId(id);
        sfuClient?.unsubscribe(id);
        this._sfuClientManager?.removePlayer(id);
        this._sendRemoteLog(`Unsubscribe ${id}`);
        delete this.muted[id];
        delete this.maxConsumerSpatialLayer[id];
        delete this.maxConsumerTemporalLayer[id];
        delete this.info[id];
        this._dispatchInfo();
    }
    allow(id) {
        this._sendRemoteLog(`Allow ${id}`);
        this.allowed[id] = true;
        this._sfuClientManager?.getSFUsByPlayerId(this.id)?.forEach((ourSFU) => ourSFU.allow(id));
    }
    disallow(id) {
        this._sendRemoteLog(`Disallow ${id}`);
        delete this.allowed[id];
        this._sfuClientManager?.getSFUsByPlayerId(this.id)?.forEach((ourSFU) => ourSFU.disallow(id));
    }
    _sendRemoteLog(message) {
        this._routerClient?.sendRemoteLog(message);
    }
    async onAddrs({ srcId, sfuAddr }) {
        const isOwnAddress = srcId === this.id;
        if (!this._sfuClientManager?.isSubscribedToPlayer(srcId))
            return;
        debugLogMajorEvent(isOwnAddress
            ? `Router assigned this user to ${sfuAddr}`
            : `Router notified addrs for player ${srcId}: ${sfuAddr}`);
        const idInfo = this.info[srcId];
        if (idInfo && idInfo.state === ConsumerState.WaitingForSFUAddress) {
            idInfo.state = ConsumerState.ConnectingToSFU;
            this._dispatchInfo();
        }
        const sfuClient = await this._sfuClientManager?.updatePlayerSFUAssignment(srcId, sfuAddr);
        if (sfuClient && !sfuClient.initialCallbacksPopulated) {
            sfuClient.initialCallbacksPopulated = true;
            sfuClient.onInfo = (info) => {
                this._sfuClientManager?.onInfo(sfuClient, info);
                Object.entries(info).forEach(([id, idInfo]) => {
                    if (!this._sfuClientManager?.isSubscribedToPlayer(id))
                        return;
                    const [primary, backup] = this._sfuClientManager?.getSFUsByPlayerId(id);
                    const isMigrating = !!backup;
                    const isPrimary = primary === sfuClient;
                    const migrationInProgress = this.info[id]?.migration === MigrationState.InProgress;
                    if (isMigrating &&
                        isPrimary &&
                        (idInfo.state !== this.info[id]?.state || migrationInProgress)) {
                        Logger.info("ignore ConsumerInfo", id, sfuClient.serverURL, JSON.stringify(idInfo), JSON.stringify(this.info[id]));
                    }
                    else {
                        this._mergeSFUInfo(id, idInfo);
                    }
                });
                this._dispatchInfo();
            };
            sfuClient.onDeviceConnectError = () => {
                const relevantIdInfo = this._sfuClientManager?.getPlayersBySFUAddr(sfuClient.serverURL);
                Logger.warn(`device connect error for ${sfuClient.serverURL} - ${JSON.stringify(relevantIdInfo)}`);
            };
            sfuClient.onDisableVideo = () => {
                this.publishEvent(PeerManagerEvent.DisableVideo);
                globalsMetricsManager.addMetricReliably("disable-video", {
                    sfu: sfuClient.serverURL,
                });
            };
            sfuClient.onClientIpInfo = (data) => {
                this.publishEvent(PeerManagerEvent.ClientIpInfo, data);
            };
            sfuClient.onMoveOff = async () => await this.moveSFU();
            sfuClient.onOverAllow = (over) => {
                if (sfuAddr === this._sfuClientManager?.getPrimarySFUByPlayerId(this.id)?.serverURL) {
                    this.publishEvent(PeerManagerEvent.OverAllow, over);
                }
            };
            sfuClient.onSelfProduceScore = ({ kind, score }) => {
                if (sfuAddr === this._sfuClientManager?.getPrimarySFUByPlayerId(this.id)?.serverURL) {
                    this.updateProduceScore(kind, score);
                }
            };
            sfuClient.onProduceClose = (kind) => {
                this._onProduceStateChange(kind, ProduceState.Ended, sfuAddr);
            };
            sfuClient.onReloadChange = (reloading) => {
                if (sfuAddr === this._sfuClientManager?.getLatestSFUAddrByPlayerId(this.id)) {
                    this._onSFUReloadChange(reloading);
                }
            };
            sfuClient.onProducerSfuConnected = (sfuAddr) => {
                this.produceLocalTracks(sfuAddr);
            };
            sfuClient.getMaxConsumerSpatialLayer = (playerId) => this.maxConsumerSpatialLayer[playerId];
            sfuClient.getLocalVideoCaptureResolution = () => this.localVideoCaptureResolution;
            sfuClient.sendDebugReport = (message, reason, remotePlayers) => {
                this.publishEvent(PeerManagerEvent.SendDebugReport, message, reason, remotePlayers ?? []);
            };
            sfuClient.onInboundBandwidthFulfillment = (bandwidthFulfillment) => {
                metricsManager.inboundBandwidthFulfillment.onInboundBandwidthFulfillment(sfuClient.serverURL, bandwidthFulfillment);
                this.bitrateManager.setBandwidthFulfillment(sfuClient.serverURL, bandwidthFulfillment);
                this.publishEvent(PeerManagerEvent.InboundBandwidthFulfillment, sfuClient.serverURL, bandwidthFulfillment);
            };
            sfuClient.onSFUProducersStats = (stats) => {
                this.publishEvent(PeerManagerEvent.ProducersStats, stats);
            };
            sfuClient.onSFUConsumersStats = (srcId, stats) => {
                this.publishEvent(PeerManagerEvent.ConsumersStats, srcId, stats);
            };
            const videoTrack = this.producedTracks.get("video");
            const pause = this.producePaused.get("video");
            if (videoTrack && pause !== undefined) {
                this.produceStart("video", videoTrack, pause);
            }
        }
    }
    async onCordonSFU({ sfuAddr }) {
        Logger.info(`SFU ${sfuAddr} is now cordoned`);
        const mySFUAddr = this._sfuClientManager?.getLatestSFUAddrByPlayerId(this.id);
        if (sfuAddr !== mySFUAddr)
            return;
        if (this.isAVInterruptible && this._routerClient) {
            Logger.info(`Player will be moved to a different SFU.`);
            const { succeed, newSfu } = await this._routerClient.moveSFU();
            if (succeed) {
                Logger.info(`Player was successfully moved to ${newSfu}`);
            }
            else {
                Logger.warn("Player could not be moved to a different SFU. Another attempt will be made when the SFU terminates");
            }
        }
    }
    produceLocalTracks(sfuAddr) {
        const produceTrackPromises = [];
        for (const [kind, track] of this.producedTracks) {
            const pause = this.producePaused.get(kind);
            if (pause !== undefined) {
                produceTrackPromises.push(this.produceStart(kind, track, pause, sfuAddr));
            }
            else {
                Logger.error(`could not get previous value of pause for produced kind`, new ErrorContext({ kind }));
            }
        }
        return Promise.all(produceTrackPromises).then(() => {
            if (this._sfuClientManager) {
                this._sfuClientManager.onLocalTracksProduced(this.id, sfuAddr);
            }
        });
    }
    playerConnectedSFU(param) {
        const { playerId, sfuClient } = param;
        const isOwnAddress = playerId === this.id;
        Logger.debug("ENTER [GatherPeerManager][playerConnectedSFU]", playerId);
        if (isOwnAddress) {
            this._onSFUReloadChange(sfuClient.reloading);
            this.publishEvent(PeerManagerEvent.OverAllow, false);
            const localTracksProducedOrTimeout = timeout(this.produceLocalTracks(sfuClient.serverURL), BACKUP_SFU_LIFESPAN_MS);
            localTracksProducedOrTimeout.finally(() => {
                if (!this._sfuClientManager?.getSFUsByPlayerId(playerId).includes(sfuClient)) {
                    Logger.debug("EXIT [GatherPeerManager][playerConnectedSFU]");
                    return;
                }
                Object.keys(this.allowed).forEach((dstId) => {
                    sfuClient.allow(dstId);
                });
            });
        }
        else {
            const subscribedTags = this._sfuClientManager?.getSubscribedTagsByPlayerId(playerId);
            if (subscribedTags) {
                sfuClient.subscribe(playerId, subscribedTags);
            }
            sfuClient.setMaxConsumerLayers(playerId, "video", this.maxConsumerSpatialLayer[playerId], this.maxConsumerTemporalLayer[playerId]);
        }
        Logger.debug("EXIT [GatherPeerManager][playerConnectedSFU]");
    }
    playerLeavingSFU(param) {
        const { playerId, sfuClient, updateConsumerInfo, consumerInfo } = param;
        Logger.debug("ENTER [GatherPeerManager][playerLeavingSFU]", playerId, updateConsumerInfo, consumerInfo);
        if (playerId === this.id) {
            for (const [kind, _] of this.producedTracks) {
                this.stopProducing(kind, sfuClient);
            }
            Object.keys(this.allowed).forEach((dstId) => {
                sfuClient.disallow(dstId);
            });
        }
        else {
            if (updateConsumerInfo && consumerInfo) {
                this._mergeSFUInfo(playerId, consumerInfo);
                this._dispatchInfo();
            }
            sfuClient.unsubscribe(playerId);
        }
        Logger.debug("EXIT [GatherPeerManager][playerLeavingSFU]");
    }
    async setMaxConsumerLayers(id, tag, spatialLayer, temporalLayer) {
        if (this.maxConsumerSpatialLayer[id] === spatialLayer &&
            this.maxConsumerTemporalLayer[id] === temporalLayer) {
            return;
        }
        if (spatialLayer === undefined) {
            delete this.maxConsumerSpatialLayer[id];
        }
        else {
            this.maxConsumerSpatialLayer[id] = spatialLayer;
        }
        if (temporalLayer === undefined) {
            delete this.maxConsumerTemporalLayer[id];
        }
        else {
            this.maxConsumerTemporalLayer[id] = temporalLayer;
        }
        const promises = this._sfuClientManager
            ?.getSFUsByPlayerId(id)
            ?.map(async (sfu) => await sfu?.setMaxConsumerLayers(id, tag, spatialLayer, temporalLayer));
        if (promises) {
            await Promise.all(promises);
        }
    }
    async setMaxConsumerLayersFiltered(id, tag, spatialLayer, temporalLayer) {
        if (this.bitrateManager.updateMaxLayersWithCheck(id, spatialLayer, temporalLayer)) {
            this.setMaxConsumerLayers(id, tag, spatialLayer, temporalLayer);
        }
    }
    setHighestPriorityStreams(ids) {
        this.bitrateManager.setHighestPriorityStreams(ids);
    }
    async moveSFU() {
        return this._routerClient?.moveSFU() ?? Promise.resolve({ succeed: false, newSfu: "" });
    }
    setMute(id, kind, muted) {
        const muteSet = this.muted[id] ?? new Set();
        this.muted[id] = muteSet;
        if (muted) {
            muteSet.add(kind);
        }
        else {
            muteSet.delete(kind);
        }
        const consumerInfo = this.info[id];
        if (consumerInfo) {
            if (kind === "video") {
                consumerInfo.video?.setMute("consumer", "video", muted);
            }
            else if (kind === "audio") {
                consumerInfo.video?.setMute("consumer", "audio", muted);
            }
            else if (kind === "screen") {
                consumerInfo.screenVideo?.setMute("consumer", "video", muted);
            }
            else if (kind === "sound") {
                consumerInfo.screenVideo?.setMute("consumer", "audio", muted);
            }
            const sfus = this._sfuClientManager?.getSFUsByPlayerId(id) || [];
            if (sfus.length === 0) {
                Logger.warn(`SFU for client ${id} not found.`);
            }
            else if (muted) {
                sfus.forEach((sfu) => sfu.pauseConsumer(id, kind));
            }
            else {
                sfus.forEach((sfu) => sfu.resumeConsumer(id, kind));
            }
            consumerInfo.muted[kind] = muted;
        }
        this._dispatchInfo();
    }
    isMuted(id, kind) {
        return Boolean(this.muted[id]?.has(kind));
    }
    setAllMuted(kind, muted, exceptedPlayer) {
        if (muted) {
            this.mutedAll.add(kind);
        }
        else {
            this.mutedAll.delete(kind);
        }
        const subscribed = this._sfuClientManager?.getSubscribedPlayers() ?? [];
        subscribed
            .filter((id) => id !== exceptedPlayer)
            .forEach((id) => {
            this.setMute(id, kind, muted);
        });
    }
    setBitrateManager(enabled) {
        if (enabled) {
            if (this._sfuClientManager) {
                this.bitrateManager.start(this.id, this._sfuClientManager, this.rtcStatsManager, true);
            }
        }
        else {
            this.bitrateManager.destroy();
        }
    }
    async _summarize() {
        const subscribed = this._sfuClientManager?.summarizeSubscribed();
        const mutedUsers = {};
        Object.entries(this.muted).forEach(([id, tags]) => {
            mutedUsers[id] = Array.from(tags);
        });
        const muted = {
            mutedAll: Array.from(this.mutedAll),
            mutedUsers,
        };
        const info = {};
        Object.entries(this.info).forEach(([id, idInfo]) => {
            info[id] = idInfo.summarize();
        });
        const SFUs = await this._sfuClientManager?.summarize();
        return {
            id: this.id,
            spaceId: this.spaceId,
            router: this._routerClient?.summarize(),
            subscribed,
            allowed: Object.keys(this.allowed),
            muted,
            SFUs,
            info,
            debugMajorEvents,
        };
    }
    getWebrtcConnectionInfo(id) {
        const sfu = this._sfuClientManager?.getPrimarySFUByPlayerId(id);
        if (!sfu)
            return;
        if (this.id === id) {
            const info = {
                serverURL: sfu?.serverURL || "",
                maxSpatialLayer: new Map(),
                transceivers: new Map(),
                connectionState: sfu?.sendTransport?.transport?.connectionState ?? "closed",
                iceTransportPolicy: sfu?.sendTransport?.iceTransportPolicy ?? "all",
            };
            for (const [kind, _] of this.producedTracks) {
                const producer = sfu?.producers.get(kind);
                info.transceivers.set(kind, producer?.rtpSender);
                info.maxSpatialLayer.set(kind, producer?.maxSpatialLayer);
            }
            return info;
        }
        else {
            const info = {
                serverURL: sfu?.serverURL ?? "",
                transceivers: new Map(),
                maxSpatialLayer: new Map(),
                connectionState: sfu?.recvTransport?.transport?.connectionState ?? "closed",
                iceTransportPolicy: sfu?.recvTransport?.iceTransportPolicy ?? "all",
            };
            const consumers = sfu?.consumers.get(id) ?? new Map();
            const maxSpatialLayer = sfu?.getMaxConsumerSpatialLayer(id, "video");
            for (const [kind, consumer] of consumers) {
                info.transceivers.set(kind, consumer?.rtpReceiver);
                info.maxSpatialLayer.set(kind, maxSpatialLayer);
            }
            return info;
        }
    }
    async debugInfo() {
        return await this._summarize();
    }
    requestServerDebug() {
        const subscribed = this._sfuClientManager?.getSubscribedPlayers() || [];
        const srcIds = [this.id].concat(subscribed);
        const srcStreamId = this.spaceId;
        this._sfuClientManager?.requestServerDebug(srcIds, srcStreamId);
        this._routerClient?.requestServerDebug(srcIds);
    }
    _dispatchInfo() {
        this.bandwidthManager.onInfo(this.info);
        this.bitrateManager.onInfo(this.info);
        this.publishEvent(PeerManagerEvent.Info, this.info);
    }
    _mergeSFUInfo(id, idInfo) {
        this.info[id] = idInfo;
        this.muted[id]?.forEach((kind) => {
            this.setMute(id, kind, true);
        });
    }
    _onSFUReloadChange = (reloading) => {
        this.publishEvent(PeerManagerEvent.ServerReloading, reloading);
        if (reloading) {
            metricsManager.stopCollection();
        }
        else {
            metricsManager.startCollection();
        }
    };
    _onProduceStateChange(kind, state, sfuAddr) {
        if (sfuAddr === undefined ||
            sfuAddr === this._sfuClientManager?.getLatestSFUAddrByPlayerId(this.id)) {
            this.publishEvent(PeerManagerEvent.ProducerStateChange, kind, state);
        }
    }
    updateProduceScore(kind, score) {
        if (this.produceScores[kind] !== score) {
            this.produceScores[kind] = score;
            this.publishEvent(PeerManagerEvent.ProduceScores, { ...this.produceScores });
        }
    }
    connectToServerIfNotConnected() { }
    simulateScenario(scenario) {
        if (scenario === "network-blip") {
            this._DEBUG_disconnect();
            this._DEBUG_reload();
        }
        else if (scenario === "network-blip-all") {
            Object.values(this._sfuClientManager?.sfuClients ?? {}).forEach((sfu) => {
                sfu._DEBUG_disconnect();
                sfu.reload();
            });
        }
        else if (scenario === "long-network-blip") {
            this._DEBUG_disconnect();
            setTimeout(() => this._DEBUG_reload(), 5000);
        }
        else if (scenario === "ice-restart") {
            this._sfuClientManager
                ?.getSFUsByPlayerId(this.id)
                .forEach((sfu) => sfu.getTransport("send")?.restartIce(IceRestartReason.Debug));
            this._sfuClientManager
                ?.getSFUsByPlayerId(this.id)
                .forEach((sfu) => sfu.getTransport("recv")?.restartIce(IceRestartReason.Debug));
        }
        else if (scenario === "transport-failure") {
            this._sfuClientManager?.getSFUsByPlayerId(this.id).forEach((sfu) => {
                const transport = sfu.getTransport("send");
                if (transport) {
                    transport.eventEmitter.publishWebRTCTransportError(WebRTCTransportErrorCode.FailedToConnect, { sfuAddr: sfu.serverURL, id: transport.id });
                }
            });
            this._sfuClientManager?.getSFUsByPlayerId(this.id).forEach((sfu) => {
                const transport = sfu.getTransport("recv");
                if (transport) {
                    transport.eventEmitter.publishWebRTCTransportError(WebRTCTransportErrorCode.FailedToConnect, { sfuAddr: sfu.serverURL, id: transport.id });
                }
            });
        }
        else if (scenario === "network-blip-while-update-consumer") {
            Object.values(this._sfuClientManager?.sfuClients ?? {}).forEach((sfu) => {
                sfu.consumers.forEach((consumers, playerId) => {
                    consumers.forEach((_consumer, kind) => {
                        sfu.pauseConsumer(playerId, kind);
                        sfu.resumeConsumer(playerId, kind);
                    });
                    sfu.signaling?._ws.disconnect();
                });
            });
        }
        else {
            throw new Error("unrecognized scenario!");
        }
    }
    addLatestSpeaker(id) {
        const sfuClient = this._sfuClientManager?.getPrimarySFUByPlayerId(id);
        if (sfuClient && sfuClient.isSubscribed(id, "audio")) {
            const idInfo = this.info[id];
            const now = Date.now();
            const ACTIVE_SPEAKER_RACE_DELAY_MS = 2000;
            if (!idInfo ||
                (!idInfo.enabled.audio &&
                    idInfo.state === ConsumerState.Connected &&
                    now - idInfo.lastAudioMutedTimestamp > ACTIVE_SPEAKER_RACE_DELAY_MS)) {
                globalsMetricsManager.addMetricReliably("gvc-unexpected-audio-active", {
                    srcUserId: id,
                    sfu: sfuClient.serverURL,
                });
                const shouldReport = gatherVideoClientDataSource.gate(GatedFeatName.ReportUnexpectedActiveSpeaking, this.id, this.spaceId);
                const LOCAL_STORAGE_KEY = "debug_UnexpectedAudioActive";
                const ONE_DAY_IN_MS = 86400000;
                if (shouldReport && window) {
                    const now = Date.now();
                    const lastReportTimestamp = parseInt(window.localStorage.getItem(LOCAL_STORAGE_KEY) ?? "", 10);
                    if (isNaN(lastReportTimestamp) ||
                        (!isNaN(lastReportTimestamp) && now - lastReportTimestamp > ONE_DAY_IN_MS)) {
                        const message = "Unexpected active speaking";
                        const reason = VIDEO_SYSTEM_AUTOMATED_REPORT;
                        this.publishEvent(PeerManagerEvent.SendDebugReport, message, reason, [id]);
                        window.localStorage.setItem("debug_UnexpectedAudioActive", now.toString());
                    }
                }
                else if (window) {
                    window.localStorage.removeItem(LOCAL_STORAGE_KEY);
                }
            }
        }
        return this.bitrateManager.latestSpeakerStack.addLatestSpeaker(id);
    }
    setStreamStatePaused(playerId, paused) {
        const idInfo = this.info[playerId];
        if (idInfo) {
            idInfo.streamStatePaused = { screen: false, video: paused };
            const sfus = this._sfuClientManager?.getSFUsByPlayerId(playerId) || [];
            if (sfus.length === 0) {
                Logger.warn(`SFU for client ${playerId} not found.`);
            }
            else if (paused) {
                sfus.forEach((sfu) => sfu.pauseConsumer(playerId, "video"));
            }
            else if (idInfo.enabled.video && !idInfo.muted.video) {
                sfus.forEach((sfu) => sfu.resumeConsumer(playerId, "video"));
            }
            this._dispatchInfo();
        }
        else {
            Logger.warn("Couldn't find idInfo for", playerId);
        }
    }
    async getConnectionReport(userId) {
        const routerReport = this._routerClient?.getConnectionReport() ?? {};
        const sfuClientManagerReport = await this._sfuClientManager?.getConnectionReport(userId);
        const mutedKinds = this.muted[userId] ?? new Set();
        const report = {
            mutedFromAllUsers: [...this.mutedAll].join(" "),
            muted: [...mutedKinds].join(" "),
            isAVInterruptible: this.isAVInterruptible,
            ...prefixKeys("router", routerReport),
            ...prefixKeys("sfuClientManager", sfuClientManagerReport ?? {}),
        };
        return prefixKeys("conn", report);
    }
    getActiveVideoElements() {
        return getActiveVideoElements(this.info);
    }
    onWebRTCTransportConnectionStateChange = (context) => {
        if (!context.direction) {
            Logger.error("Should never happen, direction is missing", context);
            return;
        }
        this.publishEvent(PeerManagerEvent.MediaConnectionStateChange, context.sfuAddr, context.direction, context.connectionState === "connected");
    };
    networkChanged() {
        this._sfuClientManager?.networkChanged();
    }
    _DEBUG_disconnect() {
        this._sfuClientManager?.getSFUsByPlayerId(this.id).forEach((sfu) => sfu._DEBUG_disconnect());
    }
    _DEBUG_reload() {
        this._sfuClientManager?.getSFUsByPlayerId(this.id).forEach((sfu) => sfu.reload());
    }
    _DEBUG_restartICE(sfu, direction) {
        this._sfuClientManager?.sfuClients[sfu]
            ?.getTransport(direction)
            ?.restartIce(IceRestartReason.Debug);
    }
    _DEBUG_recreateTransport(sfu, direction) {
        const transport = this._sfuClientManager?.sfuClients[sfu]?.getTransport(direction);
        if (transport) {
            transport.eventEmitter.publishWebRTCTransportError(WebRTCTransportErrorCode.FailedToConnect, {
                sfuAddr: sfu,
                id: transport.id,
            });
        }
    }
    _DEBUG_getSFUPlayerData(sfu) {
        this._sfuClientManager?.sfuClients[sfu]?.getSFUPlayerData();
    }
    _DEBUG_consumers(sfu) {
        this._sfuClientManager?.sfuClients[sfu]?._DEBUG_consumers();
    }
    _DEBUG_setConsumePriorities(sfu, tag, srcIds) {
        this._sfuClientManager?.sfuClients[sfu]?.setConsumePriorityByTag(tag, srcIds);
    }
    _DEBUG_profiling(sfu, start) {
        this._sfuClientManager?.sfuClients[sfu]?.requestProfiling(start);
    }
}
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "setAdaptiveStreamSettings", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "setAdaptiveStream", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "startObserve", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "stopObserve", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "produceStart", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "produceEnd", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "stopProducing", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "produceResume", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "producePause", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "onRouterConnect", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "onRouterDisconnect", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "onPacketsNotReceived", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "onConsumerDestroyed", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "onConsumerEnabled", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "start", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "restart", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "destroy", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "subscribe", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "unsubscribe", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "allow", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "disallow", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "_sendRemoteLog", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "onAddrs", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "onCordonSFU", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "produceLocalTracks", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "setMaxConsumerLayers", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "moveSFU", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "setMute", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "isMuted", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "setAllMuted", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "setBitrateManager", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "_summarize", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "requestServerDebug", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "_onProduceStateChange", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "simulateScenario", null);
__decorate([
    LoggableMethod
], GatherPeerManager.prototype, "setStreamStatePaused", null);
//# sourceMappingURL=GatherPeerManager.js.map